import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Tooltip,
  alpha,
  Badge,
} from '@mui/material';
import {
  LayoutDashboard,
  Sparkles,
  ChartBar,
  TrendingUp,
  ShoppingBag,
  Gauge,
  Briefcase,
  Users,
  Calculator,
  FileText,
  Package,
  Boxes,
  Workflow,
  Building2,
  UserCog,
  Calendar,
  CalendarDays,
  Truck,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Activity,
  Zap,
} from 'lucide-react';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 80;

const menuItems = [
  // 1: Dashboard
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  
  // 2: Call Center
  { id: 'call-center', icon: Activity, label: 'Call Center' },
  
  // 3: Approval Queue
  { id: 'approval-queue', icon: Activity, label: 'Approval Queue', highlight: true },
  
  // 4: Collaboration Hub
  { id: 'collaboration-hub', icon: Users, label: 'Collaboration Hub', highlight: true },
  
  // 5: AI Tools
  {
    id: 'ai-tools',
    icon: Sparkles,
    label: 'AI Tools',
    highlight: true,
    submenu: [
      { id: 'command-center', label: 'Command Center' },
      { id: 'workflow-monitor', label: 'Workflow Monitor' },
      { id: 'concept-generator', label: 'Concept Generator' },
      { id: 'cad-assistant', label: 'CAD Assistant' },
      { id: 'drafting-copilot', label: 'Drafting Copilot' },
      { id: 'fusion360-assistant', label: 'Fusion 360 Assistant' },
      { id: 'shop-drawing-assistant', label: 'Shop Drawing Assistant' },
      { id: 'revit-assistant', label: 'Revit Assistant' },
    ],
  },
  
  // 6: Reports & Analytics
  { id: 'reports', icon: ChartBar, label: 'Reports & Analytics' },
  
  // 7: Forecasting
  { id: 'forecasting', icon: TrendingUp, label: 'Forecasting' },
  
  // 8: PM Center
  {
    id: 'pm-center',
    icon: Briefcase,
    label: 'PM Center',
    submenu: [
      { id: 'pm-dashboard', label: 'PM Dashboard', highlight: true },
      { id: 'pm-assistant', label: 'PM Assistant' },
      { id: 'pm-jobs', label: 'PM Jobs' },
      { id: 'project-copilot', label: 'Project Copilot' },
      { id: 'resource-planner', label: 'Resource Planner' },
      { id: 'financial-intelligence', label: 'Financial Intelligence' },
    ],
  },
  
  // 9: Permits
  {
    id: 'permits',
    icon: FileText,
    label: 'Permits',
    submenu: [
      { id: 'permit-dashboard', label: 'Permit Dashboard' },
      { id: 'autonomous-permitting', label: '🤖 Autonomous Permitting' },
      { id: 'permit-agent', label: '🧠 Agent Control Center' },
      { id: 'permit-settings', label: '⚙️ Permit Settings' },
      { id: 'permits-list', label: 'All Permits' },
      { id: 'permit-new', label: 'New Permit' },
      { id: 'permit-workflows', label: 'Permit Workflows' },
    ],
  },
  
  // 10: Production Control Board
  { id: 'production-control-board', icon: Gauge, label: 'Production Control Board' },
  
  // 11: Customer Orders
  { id: 'customer-orders', icon: ShoppingBag, label: 'Customer Orders' },
  
  // 12: Clients
  { id: 'clients', icon: Users, label: 'Clients' },
  
  // 13: Projects
  { id: 'projects', icon: Briefcase, label: 'Projects' },
  
  // 14: Design
  {
    id: 'design',
    icon: Activity,
    label: 'Design',
    submenu: [
      { id: 'design-pipeline', label: 'Design Pipeline' },
      { id: 'design-orders', label: 'Design Orders' },
      { id: 'design-jobs', label: 'Design Jobs' },
      { id: 'design-calendar', label: 'Design Calendar' },
    ],
  },
  
  // 15: Estimates
  {
    id: 'estimates',
    icon: Calculator,
    label: 'Estimates',
    submenu: [
      { id: 'estimating-assistant', label: 'Estimating Assistant' },
      { id: 'estimates-main', label: 'Estimates' },
      { id: 'estimate-jobs', label: 'Estimate Jobs' },
      { id: 'split-estimates', label: 'Split Estimates' },
      { id: 'monument-estimate', label: 'Monument Estimate' },
      { id: 'document-generator', label: 'Smart Documents' },
      { id: 'smart-sheets', label: 'Smart Sheets', highlight: true },
    ],
  },
  
  // 15: Sales Orders
  {
    id: 'sales-orders',
    icon: ShoppingBag,
    label: 'Sales Orders',
    submenu: [
      { id: 'sales-order-pipeline', label: 'Sales Order Pipeline' },
      { id: 'sales-orders-list', label: 'Sales Orders' },
      { id: 'split-sales-orders', label: 'Split Sales Orders' },
    ],
  },
  
  // 16: Production
  {
    id: 'production',
    icon: Gauge,
    label: 'Production',
    submenu: [
      { id: 'production-dashboard', label: 'Production Dashboard' },
      { id: 'production-jobs', label: 'Production Jobs' },
      { id: 'production-split-jobs', label: 'Split Jobs' },
      { id: 'tm-review', label: 'T&M Review', highlight: true },
      { id: 'production-job-class', label: 'Job Class' },
      { id: 'production-job-type', label: 'Job Type' },
      { id: 'production-no-charge-type', label: 'No Charge Type' },
    ],
  },
  
  // 17: Installations
  {
    id: 'installations',
    icon: Calendar,
    label: 'Installations',
    submenu: [
      { id: 'install-dashboard', label: 'Install Dashboard' },
      { id: 'install-calendar', label: 'Install Calendar' },
      { id: 'installs', label: 'Installs' },
      { id: 'active-routes', label: 'Active Routes' },
      { id: 'routes', label: 'Routes' },
      { id: 'pickup', label: 'Pickup' },
      { id: 'install-template', label: 'Install Template' },
    ],
  },
  
  // 18: Transportation
  {
    id: 'transportation',
    icon: Truck,
    label: 'Transportation',
    submenu: [
      { id: 'drivers', label: 'Drivers' },
      { id: 'vehicles', label: 'Vehicles' },
    ],
  },
  
  // 19: Inventory
  {
    id: 'inventory',
    icon: Boxes,
    label: 'Inventory',
    submenu: [
      { id: 'inventory-assistant', label: 'Inventory Assistant' },
      { id: 'inventory-catalog', label: 'Catalog' },
      { id: 'inventory-services', label: 'Services' },
      { id: 'inventory-materials', label: 'Material Inventory' },
      { id: 'inventory-adjustment', label: 'Inventory Adjustment' },
      { id: 'inventory-audit', label: 'Audit' },
      { id: 'inventory-category', label: 'Category' },
      { id: 'inventory-sub-category', label: 'Sub Category' },
    ],
  },
  
  // 20: Purchasing
  {
    id: 'purchasing',
    icon: ShoppingBag,
    label: 'Purchasing',
    submenu: [
      { id: 'purchasing-assistant', label: 'Purchasing Assistant' },
      { id: 'vendors', label: 'All Vendors' },
      { id: 'vendor-map', label: 'Vendor Maps' },
      { id: 'vendor-types', label: 'Vendor Types' },
      { id: 'purchase-orders', label: 'Purchase Order' },
      { id: 'purchasing-proposals', label: 'Proposals' },
    ],
  },
  
  // 21: Workflows
  {
    id: 'workflows',
    icon: Workflow,
    label: 'Workflows',
    submenu: [
      { id: 'workflow-studio', label: 'Workflow Studio', highlight: true },
      { id: 'ai-workflow-builder', label: 'AI Workflow Builder', highlight: true },
      { id: 'workflow-onboarding', label: 'Onboarding Workflows' },
      { id: 'workflow-production', label: 'Production Workflows' },
      { id: 'workflow-service', label: 'Service Workflows' },
      { id: 'automations', label: 'Automations' },
      { id: 'n8n-setup', label: 'N8N Setup' },
      { id: 'custom-field-templates', label: 'Custom Field Templates' },
      { id: 'questionnaire-templates', label: 'Questionnaire Templates' },
      { id: 'custom-fields', label: 'Custom Fields' },
      { id: 'sign-agreements', label: 'Sign Agreements' },
    ],
  },
  
  // 21.5: AI Staff Scheduling
  {
    id: 'scheduling',
    icon: CalendarDays,
    label: 'Scheduling',
    submenu: [
      { id: 'production-schedule', label: 'Production Schedule', highlight: true },
      { id: 'install-schedule', label: 'Install Schedule', highlight: true },
      { id: 'schedule-proposals', label: 'Scheduler Proposals' },
    ],
  },
  
  // 22: Staff Management
  {
    id: 'staff-management',
    icon: UserCog,
    label: 'Staff Management',
    submenu: [
      { id: 'resource-planner', label: 'Resource Planner', highlight: true },
      { id: 'departments', label: 'Departments' },
      { id: 'team-members', label: 'Team Members' },
      { id: 'user-roles', label: 'User Roles' },
      { id: 'time-tracking', label: 'Time Tracking' },
      { id: 'time-intelligence', label: 'Time Intelligence', highlight: true },
      { id: 'staff-payout', label: 'Staff Payout' },
      { id: 'staff-scheduler', label: 'Staff Scheduler' },
      { id: 'skills-services', label: 'Skills & Services' },
    ],
  },
  
  // 23: CRM
  {
    id: 'crm',
    icon: Building2,
    label: 'CRM',
    submenu: [
      { id: 'accounts', label: 'Accounts' },
      { id: 'contacts', label: 'Contacts' },
      { id: 'leads', label: 'Leads' },
      { id: 'opportunities', label: 'Opportunities' },
      { id: 'calls', label: 'Calls' },
      { id: 'sms', label: 'SMS' },
      { id: 'campaigns', label: 'Campaigns' },
    ],
  },
  
  // 24: Tax Categories
  { id: 'tax-categories', icon: Calculator, label: 'Tax Categories' },
  
  // 25: Tickets
  { id: 'tickets', icon: FileText, label: 'Tickets' },
  
  // 26: Document Repository
  { id: 'document-repository', icon: FileText, label: 'Document Repository' },
  
  // 27: OHI Engine
  {
    id: 'ohi-engine',
    icon: Activity,
    label: 'OHI Engine',
    submenu: [
      { id: 'ohi-dashboard', label: 'OHI Dashboard', highlight: true },
      { id: 'ohi-overview', label: 'Overview' },
      { id: 'ohi-health-engine', label: 'Health Engine' },
      { id: 'ohi-reports', label: 'Reports' },
      { id: 'ohi-alerts', label: 'Alerts' },
      { id: 'ohi-audit', label: 'Audit' },
      { id: 'ohi-command-center', label: 'Command Center' },
      { id: 'ohi-divisions', label: 'Divisions' },
      { id: 'ohi-settings', label: 'Settings' },
      { id: 'ohi-teams', label: 'Teams' },
      { id: 'ohi-users', label: 'Users' },
    ],
  },
  
  // 28: System
  {
    id: 'system',
    icon: Settings,
    label: 'System',
    submenu: [
      { id: 'settings', label: 'Settings' },
    ],
  },
];

function Sidebar({ currentView, onViewChange, collapsed, onToggleCollapse }) {
  const [expandedMenu, setExpandedMenu] = useState(null);

  const handleMenuClick = (item) => {
    if (item.submenu) {
      setExpandedMenu(expandedMenu === item.id ? null : item.id);
    } else {
      onViewChange(item.id);
    }
  };

  const handleSubmenuClick = (submenuId) => {
    onViewChange(submenuId);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
          boxShadow: '4px 0 24px -8px rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.08)',
          minHeight: 72,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px -8px rgba(99, 102, 241, 0.5)',
              }}
            >
              <Zap size={22} color="#ffffff" />
            </Box>
            <Box>
              <Typography 
                variant="h6" 
                fontWeight={800}
                sx={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #94A3B8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                FusionPM
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>
                Project Management
              </Typography>
            </Box>
          </Box>
        )}
        {collapsed && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px -8px rgba(99, 102, 241, 0.5)',
            }}
          >
            <Zap size={22} color="#ffffff" />
          </Box>
        )}
        <IconButton 
          size="small" 
          onClick={onToggleCollapse}
          sx={{ 
            color: 'rgba(255,255,255,0.5)',
            '&:hover': { 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)' 
            },
            ...(collapsed && { display: 'none' }),
          }}
        >
          <ChevronLeft size={20} />
        </IconButton>
      </Box>

      {/* Menu Items */}
      <List sx={{ flex: 1, py: 2, px: collapsed ? 1 : 1.5 }}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentView === item.id || 
            (item.submenu && item.submenu.some(sub => currentView === sub.id));
          const isExpanded = expandedMenu === item.id;

          return (
            <Box key={item.id} sx={{ mb: 0.5 }}>
              <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleMenuClick(item)}
                    sx={{
                      minHeight: 48,
                      px: collapsed ? 2 : 2,
                      py: 1,
                      borderRadius: 3,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      ...(isActive && {
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 4,
                          height: '60%',
                          borderRadius: '0 4px 4px 0',
                          background: 'linear-gradient(180deg, #6366F1 0%, #8B5CF6 100%)',
                        },
                      }),
                      ...(item.highlight && !isActive && {
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)',
                      }),
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.06)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: collapsed ? 0 : 2,
                        justifyContent: 'center',
                        color: isActive ? '#8B5CF6' : 'rgba(255,255,255,0.5)',
                        transition: 'color 0.2s',
                      }}
                    >
                      {item.highlight ? (
                        <Badge 
                          variant="dot" 
                          color="secondary"
                          sx={{ 
                            '& .MuiBadge-dot': { 
                              width: 8, 
                              height: 8,
                              animation: 'pulse 2s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                                '50%': { transform: 'scale(1.2)', opacity: 0.7 },
                              },
                            } 
                          }}
                        >
                          <Icon size={20} />
                        </Badge>
                      ) : (
                        <Icon size={20} />
                      )}
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText 
                          primary={item.label} 
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: isActive ? 600 : 500,
                            color: isActive ? '#ffffff' : 'rgba(255,255,255,0.7)',
                          }}
                        />
                        {item.submenu && (
                          <Box sx={{ color: 'rgba(255,255,255,0.4)' }}>
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </Box>
                        )}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>
              </Tooltip>

              {/* Submenu */}
              {item.submenu && !collapsed && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2, mt: 0.5 }}>
                    {item.submenu.map((subItem) => {
                      const isSubActive = currentView === subItem.id;
                      return (
                        <ListItemButton
                          key={subItem.id}
                          onClick={() => handleSubmenuClick(subItem.id)}
                          sx={{ 
                            pl: 4.5, 
                            py: 0.75,
                            minHeight: 38,
                            borderRadius: 2,
                            position: 'relative',
                            transition: 'all 0.2s',
                            ...(isSubActive && {
                              bgcolor: 'rgba(255,255,255,0.06)',
                            }),
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.04)',
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 24,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: isSubActive ? '#8B5CF6' : 'rgba(255,255,255,0.2)',
                              transition: 'background-color 0.2s',
                            },
                          }}
                        >
                          <ListItemText 
                            primary={subItem.label}
                            primaryTypographyProps={{
                              fontSize: 13,
                              fontWeight: isSubActive ? 600 : 400,
                              color: isSubActive ? '#ffffff' : 'rgba(255,255,255,0.5)',
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>

      {/* Collapse toggle at bottom when collapsed */}
      {collapsed && (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <IconButton 
            size="small" 
            onClick={onToggleCollapse}
            sx={{ 
              color: 'rgba(255,255,255,0.5)',
              width: '100%',
              '&:hover': { 
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)' 
              },
            }}
          >
            <ChevronRight size={20} />
          </IconButton>
        </Box>
      )}
    </Drawer>
  );
}

export default Sidebar;
